import { usePlaceholderHook } from "@/hooks/use-placeholder.hook.ts";
import { UserMagnifyingGlass } from "@/icons/UserMagnifyingGlass.tsx";
import { LoraModel } from "@/models/lora.model.ts";
import { getLoras } from "@/services/lora.service.ts";
import classes from "@/ui/shared/StyleSelect.module.css";
import { ActionIcon, Button, Image, Popover, Tooltip } from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

export function LoraFilter({
  lora,
  setLora,
}: {
  lora: LoraModel | undefined;
  setLora: (value: LoraModel | undefined) => void;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [opened, { close, toggle }] = useDisclosure(false);
  const [searchParams] = useSearchParams();
  const loraParams = {
    limit: 0,
    sort: "-created_at",
    "filter[status]": "published",
  };

  const { data: loraData, isLoading } = useQuery({
    queryKey: ["loras", loraParams],
    queryFn: () => getLoras(loraParams),
    retry: 1,
  });
  const loras: LoraModel[] = loraData?.data || [];
  const placeholder = usePlaceholderHook();

  useEffect(() => {
    if (!loras) {
      return;
    }
    const selectedLoraUuid = searchParams.get("character");
    const selectedLora = loras.find((lora) => lora.uuid === selectedLoraUuid);

    if (!selectedLora) {
      return;
    }

    if (selectedLora) {
      setLora(selectedLora);
    }
  }, [loras, setLora, searchParams]);

  function handleOnClick(event: MouseEvent) {
    if (
      dropdownRef.current &&
      !event.composedPath().includes(dropdownRef.current)
    ) {
      close();
    }
  }

  useEffect(() => {
    if (!opened) return;
    document.body.addEventListener("click", handleOnClick);
    return () => {
      document.body.removeEventListener("click", handleOnClick);
    };
  }, [opened]);

  useHotkeys([["escape", () => close]]);

  return (
    <>
      <Popover
        opened={opened}
        onClose={close}
        width={380}
        shadow="md"
        withArrow
      >
        <Popover.Target>
          <Tooltip
            disabled={opened}
            label="Filter by character"
            position="bottom"
            withArrow
          >
            <ActionIcon
              onClick={toggle}
              size="lg"
              loading={isLoading}
              aria-label="Filter by character"
            >
              <UserMagnifyingGlass size={20} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown ref={dropdownRef} onMouseLeave={close}>
          <div className="flex flex-wrap gap-2 items-center justify-center">
            {loras.map((loraEntry) => (
              <button
                type="button"
                key={loraEntry.id}
                onClick={() => {
                  if (loraEntry.id === lora?.id) {
                    setLora(undefined);
                    close();
                    return;
                  }
                  setLora(loraEntry);
                  close();
                }}
                className="flex flex-col items-center p-1 relative cursor-pointer"
              >
                {loraEntry.id === lora?.id && (
                  <div className={classes.indicator} />
                )}
                <Image
                  className="z-[2]"
                  src={loraEntry.cover || placeholder}
                  radius="sm"
                  w={100}
                  h={100}
                />

                {loraEntry.id === lora?.id && (
                  <span className={classes.selected}>{loraEntry.name}</span>
                )}
                {loraEntry.id !== lora?.id && (
                  <span className={classes.deselected}>{loraEntry.name}</span>
                )}
              </button>
            ))}
          </div>
          <Button
            mt={8}
            variant="default"
            onClick={() => {
              setLora(undefined);
              close();
            }}
            fullWidth={true}
          >
            Reset
          </Button>
        </Popover.Dropdown>
      </Popover>
    </>
  );
}
